import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEPLOYMENT_KIND_LABELS,
  ROBOT_STATUS_LABELS,
  type CalendarEventDto,
  type RobotStatus,
} from '@bhops/shared';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { extractApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useToast } from '@/components/Toast';
import { kstYmdHyphen } from '@/lib/format-date';
import {
  AssignPickList,
  AssignStage,
  PickPanel,
  StripCard,
  chipCenters,
  chipFrom,
  compareRobotNumber,
  lineHeading,
  type DragLoad,
  type FlyRequest,
  type PickSection,
  type StageHandle,
  type StageLine,
} from './assignment-stage';

// 로봇 배정 — 이벤트(2026-11-02 오너 — "로봇 배정도 이벤트용 배정 기능이 필요해", "제조에서 이벤트용 로봇을
// 미리 배정해줘야해"). 캘린더에서 이벤트를 추가할 때 적은 모델 · 대수만큼 재고 로봇을 고른다. 배정한
// 로봇은 이벤트에 예약돼 계약 배정 후보에서 빠지고, 캘린더 [이벤트 회수] 우클릭 [회수 완료]로 풀린다.
//
// 2026-11-04 — 배정 화면이 '장착대'(시안 5)로 바뀌며 이벤트도 같은 짜임이 됐다. 위 카드 띠에 보라색
// '✦ 이벤트' 표시를 단 카드(오너 "이벤트는 이렇게 표시하게 해주고"), 고르면 왼쪽 창고 로봇 · 가운데 무대.

export const eventNeed = (e: CalendarEventDto) => e.robotLines.reduce((n, l) => n + l.quantity, 0);

/** 배정할 이벤트 — 회수를 마치지 않았고 출고할 로봇이 있는 것, 설치일 순. */
export function assignableEvents(items: CalendarEventDto[] | undefined): CalendarEventDto[] {
  return (items ?? [])
    .filter((e) => !e.returned && eventNeed(e) > 0)
    .sort((a, b) => a.installDate.localeCompare(b.installDate));
}

const md = (iso: string, time: string | null) => {
  const ymd = kstYmdHyphen(iso) ?? '';
  return `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}${time ? ` ${time}` : ''}`;
};

/** 보라색 '✦ 이벤트' 표시 — 캘린더 이벤트 색. */
export function EventTag() {
  return (
    <span style={eventTag} data-testid="assign-event-tag">
      <Icon name="sparkles" size={11} aria-hidden /> 이벤트
    </span>
  );
}

function EventBadge({ have, need }: { have: number; need: number }) {
  const done = have >= need;
  return (
    <span
      data-testid="assign-event-badge"
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 'var(--r-pill)',
        whiteSpace: 'nowrap',
        ...(done
          ? { background: 'var(--bg-success-subtle)', color: 'var(--fg-on-success-tint)' }
          : have > 0
            ? { background: 'var(--bg-warning-subtle)', color: 'var(--fg-on-warning-tint)' }
            : { background: 'var(--bg-subtle)', color: 'var(--fg-muted)' }),
      }}
    >
      {done ? '배정 완료' : have > 0 ? `배정 중 ${have}/${need}` : '배정 필요'}
    </span>
  );
}

// ── 위: 이벤트 카드 ──────────────────────────────────────────────────────

export function EventStripCard({
  event: e,
  selected,
  onSelect,
}: {
  event: CalendarEventDto;
  selected: boolean;
  onSelect: () => void;
}) {
  const need = eventNeed(e);
  const have = e.robots.length;
  return (
    <StripCard
      testId="assign-event-card"
      selected={selected}
      onSelect={onSelect}
      date={e.installDate}
      done={have >= need}
      name={<span title={e.location}>{e.location}</span>}
      // '✦ 이벤트' 표시는 이름 줄이 아니라 아랫줄 맨 앞에 — 이름이 표시에 밀려 잘리지 않게.
      sub={
        <span title={e.content} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <EventTag />
          {md(e.returnDate, null)} 회수,{' '}
          {e.robotLines.map((l) => `${l.modelName} ${l.quantity}대`).join(', ')}
        </span>
      }
      assigned={have}
      required={need}
      badge={<EventBadge have={have} need={need} />}
    />
  );
}

// ── 아래: 이벤트를 골랐을 때의 장착대 ────────────────────────────────────

export function EventBoard({
  event: e,
  variantAxisLabelByModelId,
}: {
  event: CalendarEventDto;
  /** 줄 제목(정방향 · 역방향 · 방향 미정 · 제퍼)을 정할 때 — 구분 축이 '방향'인 모델만 방향으로 부른다. */
  variantAxisLabelByModelId?: Map<string, string | null>;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [drafts, setDrafts] = useState<string[]>([]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [dragLoad, setDragLoad] = useState<DragLoad | null>(null);
  const [fly, setFly] = useState<FlyRequest | null>(null);
  const [keyword, setKeyword] = useState('');
  const stageRef = useRef<StageHandle>(null);

  const candidatesQ = useQuery({
    queryKey: ['assignments', 'event-candidates', e.id],
    queryFn: () => scheduleDatesApi.eventCandidates(e.id),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['assignments'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['robot-deployments'] });
  };
  const assign = useMutation({
    mutationFn: (robotIds: string[]) => scheduleDatesApi.assignEventRobots(e.id, robotIds),
    onSuccess: (res) => {
      setDrafts([]);
      invalidate();
      stageRef.current?.burst();
      toast.success(
        res.deployed
          ? '이벤트에 로봇을 배정했습니다. 이미 설치를 마친 이벤트라 출고로 함께 기록했습니다.'
          : '이벤트에 로봇을 배정했습니다.',
      );
    },
    onError: (err) => toast.error(extractApiError(err), '배정하지 못했습니다'),
  });
  const release = useMutation({
    mutationFn: (robotId: string) => scheduleDatesApi.releaseEventRobot(e.id, robotId),
    onSuccess: (res) => {
      invalidate();
      toast.success(
        res.undeployed
          ? '배정을 풀고 그 로봇의 출고 기록도 되돌렸습니다. 로봇이 다시 재고 후보가 됩니다.'
          : '배정을 풀었습니다. 로봇이 다시 재고 후보가 됩니다.',
      );
    },
    onError: (err) => toast.error(extractApiError(err), '배정을 풀지 못했습니다'),
  });

  const items = useMemo(() => candidatesQ.data?.items ?? [], [candidatesQ.data]);
  const byId = useMemo(() => new Map(items.map((r) => [r.id, r])), [items]);
  const need = eventNeed(e);
  const have = e.robots.length;

  const roomFor = (modelId: string, extra: string[] = drafts): number => {
    const line = e.robotLines.find((l) => l.modelId === modelId);
    if (!line) return 0;
    const saved = e.robots.filter((r) => r.modelId === modelId).length;
    const drafted = extra.filter((id) => byId.get(id)?.modelId === modelId).length;
    return line.quantity - saved - drafted;
  };

  /** 고른(끈) 로봇을 맞는 빈자리에 넣는다 — 자리가 없는 모델은 빼고 알린다. */
  function put(ids: string[], from: FlyRequest['from']): void {
    const next = [...drafts];
    let full = 0;
    for (const id of ids.map((x) => x.replace(/^r:/, ''))) {
      const r = byId.get(id);
      if (!r || next.includes(id)) continue;
      if (roomFor(r.modelId, next) <= 0) {
        full += 1;
        continue;
      }
      next.push(id);
    }
    const added = next.filter((id) => !drafts.includes(id));
    setDrafts(next);
    setPicked(new Set());
    if (added.length > 0) {
      setFly({
        seq: Date.now(),
        from,
        targets: added.map((id) => ({ flyKey: `robot:${id}`, label: byId.get(id)?.label ?? '' })),
      });
    }
    if (full > 0) toast.error(`더 넣을 자리가 없는 로봇 ${full}대는 빼 두었습니다.`);
  }

  function fill(): void {
    const next = [...drafts];
    for (const l of e.robotLines) {
      let room = roomFor(l.modelId, next);
      const pool = items
        .filter((r) => r.modelId === l.modelId && !next.includes(r.id))
        .sort(
          (a, b) =>
            Number(b.status === 'READY_TO_SHIP') - Number(a.status === 'READY_TO_SHIP') ||
            a.label.localeCompare(b.label, 'ko', { numeric: true }),
        );
      for (const r of pool) {
        if (room <= 0) break;
        next.push(r.id);
        room -= 1;
      }
    }
    const added = next.filter((id) => !drafts.includes(id));
    if (added.length === 0) {
      toast.error('넣을 수 있는 재고 로봇이 없습니다.');
      return;
    }
    const pts = chipCenters();
    setDrafts(next);
    setFly({
      seq: Date.now(),
      from: (label) => pts.get(label) ?? null,
      targets: added.map((id) => ({ flyKey: `robot:${id}`, label: byId.get(id)?.label ?? '' })),
    });
  }

  const lines: StageLine[] = e.robotLines.map((l) => {
    const saved = e.robots.filter((r) => r.modelId === l.modelId);
    const mine = drafts.filter((id) => byId.get(id)?.modelId === l.modelId);
    const empty = Math.max(0, l.quantity - saved.length - mine.length);
    const draggedModel = dragLoad?.robotIds.map((id) => byId.get(id)?.modelId).find(Boolean);
    return {
      key: l.modelId,
      modelCode: l.modelName,
      // 이벤트 줄에는 희망 방향이 없다 — GEN2 면 '방향 미정', 제퍼면 '제퍼'.
      heading: lineHeading(l.modelName, null, variantAxisLabelByModelId?.get(l.modelId)),
      title: l.modelName,
      count: `${saved.length + mine.length}/${l.quantity}대`,
      hint: draggedModel ? (draggedModel === l.modelId ? 'ok' : 'off') : undefined,
      slots: [
        ...saved.map((r) => ({
          key: `saved:${r.id}`,
          kind: 'robot' as const,
          tone: 'ok' as const,
          plate: (
            <span className="asg-sn" data-plain>
              {r.label}
            </span>
          ),
          sub: r.modelName,
          testId: 'assign-event-robot',
          action: (
            <button
              type="button"
              className="asg-fig-x"
              aria-label={`${r.label} 배정 풀기`}
              title="배정 풀기"
              disabled={release.isPending}
              onClick={() => release.mutate(r.id)}
            >
              <Icon name="x" size={13} aria-hidden />
            </button>
          ),
        })),
        ...mine.map((id) => {
          const r = byId.get(id)!;
          return {
            key: `draft:${id}`,
            kind: 'robot' as const,
            draft: true,
            tone: 'ok' as const,
            flyKey: `robot:${id}`,
            plate: (
              <span className="asg-sn" data-plain>
                {r.label}
              </span>
            ),
            sub: ROBOT_STATUS_LABELS[r.status as RobotStatus] ?? r.status,
            action: (
              <button
                type="button"
                className="asg-fig-x"
                aria-label={`${r.label} 빼기`}
                title="빼기"
                onClick={() => setDrafts((d) => d.filter((x) => x !== id))}
              >
                <Icon name="x" size={13} aria-hidden />
              </button>
            ),
          };
        }),
        ...Array.from({ length: empty }, (_, i) => ({
          key: `empty:${l.modelId}:${i}`,
          kind: 'empty' as const,
          sub: `아무 ${l.modelName}`,
          title: l.modelName,
          onClick: () => {
            const ids = [...picked].filter(
              (x) => byId.get(x.replace(/^r:/, ''))?.modelId === l.modelId,
            );
            if (ids.length > 0) put(ids, chipFrom());
          },
        })),
      ],
    };
  });

  const k = keyword.replace('#', '').trim().toLowerCase();
  const sections: PickSection[] = e.robotLines.map((l) => ({
    key: l.modelId,
    title: (
      <>
        {l.modelName}
        <span style={{ fontWeight: 500 }}>남은 자리 {Math.max(0, roomFor(l.modelId))}</span>
      </>
    ),
    rows: items
      .filter(
        (r) =>
          r.modelId === l.modelId &&
          !drafts.includes(r.id) &&
          (!k || r.label.toLowerCase().includes(k)),
      )
      // 서버는 Short SN 을 글자로 정렬해 1000 이 343 앞에 선다 — 번호를 숫자로 견준다.
      .sort((a, b) => compareRobotNumber(a.label, b.label))
      .map((r) => ({
        id: `r:${r.id}`,
        label: r.label,
        testId: 'event-candidate-row',
        node: (
          <>
            <span className="asg-chip-top">
              <span className="asg-sn" data-plain>
                {r.label}
              </span>
            </span>
            <span className="asg-chip-meta">
              {l.modelName}, {ROBOT_STATUS_LABELS[r.status as RobotStatus] ?? r.status}
            </span>
          </>
        ),
      })),
  }));
  const rowCount = sections.reduce((n, s) => n + s.rows.length, 0);

  return (
    <div className="asg-board" data-testid="assign-board">
      <PickPanel
        count={rowCount}
        pickedCount={picked.size}
        onPutPicked={() => put([...picked], chipFrom())}
        keyword={keyword}
        onKeyword={setKeyword}
      >
        {candidatesQ.isLoading ? (
          <div className="asg-pick-note">불러오는 중…</div>
        ) : candidatesQ.isError ? (
          <div className="asg-pick-note" style={{ color: 'var(--fg-danger)' }}>
            후보를 불러오지 못했습니다.
          </div>
        ) : (
          <AssignPickList
            sections={sections}
            picked={picked}
            onPickedChange={setPicked}
            onPut={(ids) => put(ids, chipFrom())}
            loadOf={(ids) => ({ robotIds: ids.map((x) => x.replace(/^r:/, '')), planIds: [] })}
            onDragLoad={setDragLoad}
            canDrag
            empty={<div className="asg-pick-note">보낼 수 있는 재고 로봇이 없습니다.</div>}
            testId="event-candidates"
          />
        )}
      </PickPanel>
      <AssignStage
        ref={stageRef}
        testId="assign-stage"
        canDrop
        dragging={!!dragLoad}
        flyRequest={fly}
        lines={lines}
        onDrop={(load, _slot, point) => {
          setDragLoad(null);
          put(
            load.robotIds.map((id) => `r:${id}`),
            () => point,
          );
        }}
        header={
          <header className="asg-stage-head">
            <div className="asg-stage-title">
              <EventTag />
              <h2>{e.location}</h2>
              <EventBadge have={have} need={need} />
            </div>
            <div className="asg-kv">
              <span>
                유형<b>{DEPLOYMENT_KIND_LABELS[e.kind]}</b>
              </span>
              <span>
                설치
                <b>
                  {md(e.installDate, e.installTime)}
                  {e.installed ? ' (완료)' : ''}
                </b>
              </span>
              <span>
                회수<b>{md(e.returnDate, e.returnTime)}</b>
              </span>
              {e.content && (
                <span>
                  내용<b>{e.content}</b>
                </span>
              )}
            </div>
          </header>
        }
        notes={
          <div className="asg-stage-note">
            배정한 로봇은 회수를 마칠 때까지 계약 배정 후보에서 빠집니다.
          </div>
        }
        hint="왼쪽 로봇을 여기로 끌어다 놓으면 맞는 빈자리에 들어갑니다."
        footer={
          <div className="asg-save">
            <span className="asg-save-text">
              {drafts.length > 0
                ? `저장 전 ${drafts.length}대. 저장해야 배정됩니다.`
                : `배정 ${have}/${need}대`}
            </span>
            {drafts.length > 0 && (
              <button
                type="button"
                className="asg-btn"
                data-variant="ghost"
                onClick={() => setDrafts([])}
              >
                되돌리기
              </button>
            )}
            <button
              type="button"
              className="asg-btn"
              onClick={fill}
              disabled={have + drafts.length >= need || candidatesQ.isLoading}
            >
              맞는 로봇으로 채우기
            </button>
            <button
              type="button"
              className="asg-btn"
              data-variant="primary"
              data-testid="event-assign-submit"
              disabled={drafts.length === 0 || assign.isPending}
              onClick={() => assign.mutate(drafts)}
            >
              {assign.isPending ? '배정 중…' : `배정 저장 (${drafts.length}대)`}
            </button>
          </div>
        }
      />
    </div>
  );
}

const eventTag: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  flex: 'none',
  gap: 3,
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--cal-event-solid)',
  color: 'var(--cal-event-solid-fg)',
  fontSize: 10,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};
